import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { Calendar } from '../../models/Calendar'
import { RecordFactory } from '../../models/RecordFactory'
import { intToColorString } from '../../utils/colors'
import { ComponentData } from '../../utils/typing'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Modal from '../app/design/Modal'
import { Checkbox } from '../common/fields/Checkbox'
import { ComboBox } from '../common/fields/ComboBox'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async () => {
    await RecordFactory.getInstance().fetchCalendars()

    const [calendars, credentials] = await Promise.all([
        RecordFactory.getInstance().loadCalendars(),
        RecordFactory.getInstance().loadCredentials('google'),
    ])
    return { calendars, credentials }
}

const render = ({ base, data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const navigate = usePSNavigate()
    const user = base.me

    const [fromSearchValue, setFromSearchValue] = useState('')
    const [toSearchValue, setToSearchValue] = useState('')
    const [changes, setChanges] = useState(false)
    const [showDisconnectModal, setShowDisconnectModal] = useState(false)

    const params = useParams<{ credentialID?: string }>()
    const credential = data.credentials.find((credential) => credential.id === params?.credentialID)

    const [calendars, setCalendars] = useState<Calendar[]>(
        data.calendars
            .filter((calendar) => calendar.credential === credential?.id)
            .sort((lhs, rhs) => (lhs.summary ?? '').localeCompare(rhs.summary ?? '')) ?? []
    )
    const [hideClientInformation, setHideClientInformation] = useState<boolean>(
        data.calendars.find((calendar) => calendar.credential === credential?.id)?.hipaa ?? false
    )
    const [onlyMine, setOnlyMine] = useState(
        data.calendars.find((calendar) => calendar.credential === credential?.id)?.onlyMine ?? false
    )

    const calendarItems = useMemo(() => {
        return calendars
            .filter((calendar) => !calendar.readOnly)
            .map((calendar) => ({
                id: calendar.id || '',
                title: calendar.summary || '',
                tertiaryView: (
                    <div
                        className="size-4 rounded-full"
                        style={{ backgroundColor: intToColorString(calendar.color ?? 0) }}
                    />
                ),
                calendar,
            }))
    }, [calendars])

    const fromCalendarItems = useMemo(() => {
        return calendarItems.map((item) => ({
            ...item,
            isChecked: item.calendar.syncFrom,
        }))
    }, [calendarItems])

    const offerOnlyMine =
        onlyMine || user?.role === 'admin' || user?.type === 'company' || user?.type === 'client'

    const onDisconnectSync = async () => {
        setShowDisconnectModal(false)
        if (!credential || !calendars) return

        base.showProgressBar()

        const newCalendars = calendars.map((calendar) =>
            calendar.clone({
                sync_from: false,
                sync_to: false,
            })
        )

        await Promise.all([
            RecordFactory.getInstance().sendCalendars(newCalendars),
            RecordFactory.getInstance().cancelCredential(credential),
        ])

        base.hideProgressBar()
        navigate('/settings/calendar/sync')
    }

    const onSave = async () => {
        base.showProgressBar()

        const newCalendars = calendars.map((calendar) =>
            calendar.clone({
                hipaa: hideClientInformation,
                only_mine: onlyMine,
            })
        )

        await RecordFactory.getInstance().sendCalendars(newCalendars)

        base.hideProgressBar()
        navigate('/settings/calendar/sync')
    }

    const updateHideClientInfo = (hide: boolean) => {
        if (hideClientInformation === hide) return
        setHideClientInformation(hide)
        setChanges(true)
    }

    const updateOnlyMine = (only: boolean) => {
        if (onlyMine === only) return
        setOnlyMine(only)
        setChanges(true)
    }

    const handleSelectFromCalendar = (item: (typeof calendarItems)[number] | null) => {
        if (item === null) {
            return
        }
        setCalendars(
            calendars.map((calendar) =>
                calendar.id === item.id
                    ? calendar.clone({ sync_from: !calendar.syncFrom })
                    : calendar
            )
        )
        setChanges(true)
    }

    const handleSelectToCalendar = (item: (typeof calendarItems)[number] | null) => {
        if (item === null) {
            return
        }
        setCalendars(
            calendars.map((calendar) =>
                calendar.id === item.id ? calendar.clone({ sync_to: !calendar.syncTo }) : calendar
            )
        )

        setChanges(true)
    }
    const selectedToCalendarID = calendarItems.find((item) => item.calendar.syncTo === true)?.id

    return (
        <WorkflowPage
            title="Google Calendar"
            footerContent={
                <div className="flex w-full items-center justify-between">
                    <div>
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            disabled={!credential}
                            onClick={() => setShowDisconnectModal(true)}
                        >
                            Disconnect
                        </Button>
                    </div>
                    <div>
                        <Button intent="primary" disabled={!changes} onClick={onSave}>
                            Save
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start">
                <WorkflowPage.Card>
                    <WorkflowPage.IconSection
                        title="Connect calendars"
                        icon="calendarRefresh.large"
                        description="Appointments scheduled with you in PocketSuite will appear on the calendars selected"
                    >
                        <div className="space-y-3">
                            <ComboBox
                                searchValue={fromSearchValue}
                                onSearchValueChange={setFromSearchValue}
                                items={fromCalendarItems}
                                onSelectItem={handleSelectFromCalendar}
                                placeholder="Search calendars..."
                                className="w-full"
                                size="medium"
                            />
                            {calendarItems
                                .filter((calendar) => calendar.calendar.syncFrom === true)
                                .map((calendar) => (
                                    <div
                                        key={calendar.id}
                                        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-gray-100 p-3 px-4"
                                    >
                                        <div className="flex items-center gap-x-2">
                                            <div
                                                className="size-4 rounded-full"
                                                style={{
                                                    backgroundColor: calendar.calendar?.color
                                                        ? intToColorString(calendar.calendar.color)
                                                        : '',
                                                }}
                                            />
                                            <span>{calendar.calendar.summary} </span>
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Show PocketSuite appointments on"
                        icon="calendar.large"
                        description="Appointments scheduled with you in PocketSuite will appear on this calendar"
                    >
                        <SearchableSelect
                            searchValue={toSearchValue}
                            onSearchValueChange={setToSearchValue}
                            items={calendarItems}
                            selectedItemID={selectedToCalendarID}
                            onSelectItem={handleSelectToCalendar}
                            placeholder="Select a calendar..."
                            className="w-full"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Hide client information"
                        icon="eyeOff.large"
                        hasHoverEffect
                        description="Client info won't appear for appts shown on your personal calendar"
                        secondaryView={
                            <Checkbox
                                isChecked={hideClientInformation}
                                onChange={updateHideClientInfo}
                            />
                        }
                    />

                    {offerOnlyMine && (
                        <WorkflowPage.IconSection
                            title="Sync only my appts"
                            icon="refresh.large"
                            hasHoverEffect
                            description="Only sync appts. I'm assigned to"
                            secondaryView={
                                <Checkbox isChecked={onlyMine} onChange={updateOnlyMine} />
                            }
                        />
                    )}
                </WorkflowPage.Card>
            </div>

            <Modal
                isOpen={showDisconnectModal}
                title="Disconnect Google Calendar"
                onClose={() => setShowDisconnectModal(false)}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="secondary" onClick={() => setShowDisconnectModal(false)}>
                            Cancel
                        </Button>
                        <Button intent="destructive" onClick={onDisconnectSync}>
                            Disconnect
                        </Button>
                    </div>
                }
            >
                Are you sure you want to disconnect Google Calendar sync? This will remove all
                calendar connections.
            </Modal>

            <NavigationBlockerPrompt when={changes} />
        </WorkflowPage>
    )
}

export const ExternalCalendarSelectPage = usePSComponent(render, viewWillAppear)
